import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicPageLayout from '../components/landing-page/PublicPageLayout';

/** 20% off annual: yearly total rounded up, then effective monthly (matches landing demo logic). */
function annualPerMonthRounded(monthly: number): number {
  const yearlyDiscounted = Math.ceil(monthly * 12 * 0.8);
  return Math.ceil(yearlyDiscounted / 12);
}

const TIER1_MONTHLY = 23;
const TIER2_MONTHLY = 59;
const TIER1_ANNUAL = annualPerMonthRounded(TIER1_MONTHLY);
const TIER2_ANNUAL = annualPerMonthRounded(TIER2_MONTHLY);

const visitorAccentClass = 'font-semibold text-sky-400';

export default function PurchasePage() {
  const [annual, setAnnual] = useState(false);

  return (
    <PublicPageLayout
      title="Plans & pricing"
      subtitle="Choose coverage for your site traffic. All plans include embeddable pricing tables, session analytics, and A/B-ready variations for PricingTable AI."
      contentMaxWidthClassName="max-w-6xl"
    >
      <div
        className="rounded-2xl border border-gray-200 bg-gray-50 p-6 shadow-sm dark:border-gray-700 dark:bg-boxdark"
        data-pricing-table
      >
        <h2 className="text-center text-2xl font-bold text-blue-600 dark:text-blue-400 sm:text-3xl">
          Subscribe to PricingTable AI
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-gray-600 dark:text-gray-300">
          Run live pricing experiments, measure how visitors respond, and roll out winners with confidence.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Monthly</span>
          <label className="relative inline-block h-[34px] w-[60px] cursor-pointer">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={annual}
              onChange={(e) => setAnnual(e.target.checked)}
              aria-label="Toggle annual billing"
            />
            <span className="absolute inset-0 rounded-full bg-gray-300 transition peer-checked:bg-blue-600 dark:bg-gray-600" />
            <span className="absolute bottom-1 left-1 h-[26px] w-[26px] rounded-full bg-white transition peer-checked:translate-x-[26px]" />
          </label>
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            Annual
            <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-xs font-semibold text-white">
              Save 20%
            </span>
          </span>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-3">
          {/* Starter */}
          <div className="relative flex min-w-0 flex-col rounded-[10px] border-2 border-gray-200 bg-white p-4 transition hover:-translate-y-1 hover:shadow-lg dark:border-gray-600 dark:bg-boxdark-2">
            <div className="mb-4 text-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Starter</h3>
              <div className="mt-2 flex min-h-[3rem] items-baseline justify-center gap-1">
                {!annual ? (
                  <>
                    <span className="text-base font-semibold text-gray-900 dark:text-white">$</span>
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">{TIER1_MONTHLY}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">/month</span>
                  </>
                ) : (
                  <>
                    <span className="text-base font-semibold text-gray-900 dark:text-white">$</span>
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">{TIER1_ANNUAL}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">/month</span>
                  </>
                )}
              </div>
              {annual && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Billed as ${Math.ceil(TIER1_MONTHLY * 12 * 0.8)}/year
                </p>
              )}
            </div>
            <ul className="mb-4 flex-1 space-y-2 border-t border-gray-100 pt-2 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-300">
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                <span className={visitorAccentClass}>Up to 3,000 website visitors / month</span>
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                1 active pricing table &amp; core A/B variations
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Embeddable widget &amp; install snippet
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Session &amp; conversion analytics
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Email support
              </li>
            </ul>
            <Link
              to="/signup"
              className="block w-full rounded-lg bg-blue-600 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Get started
            </Link>
          </div>

          {/* Growth — featured */}
          <div className="relative flex min-w-0 flex-col rounded-[10px] border-2 border-blue-600 bg-white p-4 shadow-md shadow-blue-600/15 transition hover:-translate-y-1 hover:shadow-xl dark:bg-boxdark-2 md:scale-[1.02]">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 px-2.5 py-1 text-[0.65rem] font-semibold text-white">
              Most popular
            </div>
            <div className="mb-4 text-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Growth</h3>
              <div className="mt-2 flex min-h-[3rem] items-baseline justify-center gap-1">
                {!annual ? (
                  <>
                    <span className="text-base font-semibold text-gray-900 dark:text-white">$</span>
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">{TIER2_MONTHLY}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">/month</span>
                  </>
                ) : (
                  <>
                    <span className="text-base font-semibold text-gray-900 dark:text-white">$</span>
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">{TIER2_ANNUAL}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">/month</span>
                  </>
                )}
              </div>
              {annual && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Billed as ${Math.ceil(TIER2_MONTHLY * 12 * 0.8)}/year
                </p>
              )}
            </div>
            <ul className="mb-4 flex-1 space-y-2 border-t border-gray-100 pt-2 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-300">
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                <span className={visitorAccentClass}>Up to 30,000 website visitors / month</span>
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Multiple pricing tables &amp; richer variation sets
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                AI-assisted optimization workflows &amp; findings
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Advanced analytics &amp; experiment history
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Priority support
              </li>
            </ul>
            <Link
              to="/signup"
              className="block w-full rounded-lg bg-blue-600 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Get started
            </Link>
          </div>

          {/* Enterprise */}
          <div className="relative flex min-w-0 flex-col rounded-[10px] border-2 border-gray-200 bg-white p-4 transition hover:-translate-y-1 hover:shadow-lg dark:border-gray-600 dark:bg-boxdark-2">
            <div className="mb-4 text-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Enterprise</h3>
              <div className="mt-2 flex min-h-[3rem] items-center justify-center">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">Contact sales</span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Custom annual agreements</p>
            </div>
            <ul className="mb-4 flex-1 space-y-2 border-t border-gray-100 pt-2 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-300">
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                <span className={visitorAccentClass}>Unlimited website visitors / month</span>
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Unlimited tables, environments &amp; team seats
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Dedicated onboarding &amp; solution design
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Security review, SLAs &amp; custom integrations
              </li>
              <li className="relative pl-5 before:absolute before:left-0 before:text-emerald-500 before:content-['✓']">
                Named customer success contact
              </li>
            </ul>
            <Link
              to="/support"
              className="block w-full rounded-lg bg-blue-600 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Contact sales
            </Link>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Visitor limits apply to tracked pricing-page sessions per calendar month. Questions?{' '}
          <Link to="/support" className="font-medium text-yellow-600 hover:text-yellow-500 dark:text-yellow-400">
            Contact support
          </Link>
          .
        </p>
      </div>
    </PublicPageLayout>
  );
}
